import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import useVisiblePolling from "../hooks/useVisiblePolling";
import {
  QUEUE_POLL_MS, STATUS_CLASS, STATUS_LABELS, assistError, assistSessionPath, endedSummary, formatDateTime,
  canInitiateAssist, coTechniciansText, historyTechnicians, isInvited, notifyQueueChanged, presentTechnicians, sessionDuration, waitingSince,
} from "../lib/assist";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { notificationPermission, requestNotificationPermission } from "../lib/assistAlerts";
import AssistNotesList from "../components/assist/AssistNotesList";
import InviteDialog from "../components/assist/InviteDialog";
import { toast } from "sonner";
import { AlertTriangle, Bell, BellOff, ChevronRight, Headset, Loader2, Mic, MonitorUp, RefreshCw, Send, UserPlus } from "lucide-react";

// The queue also shows, on top, the assistances the technician has taken and not finished: one place to work from. The queue is
// the technicians' (support:handle); a supervisor with support:view_all alone opens on the history.
const TABS = [
  { id: "queue", label: "File d'attente", handlerOnly: true },
  { id: "history", label: "Historique" },
  { id: "notes", label: "Comptes rendus" },
];

const StatusChip = ({ status }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${STATUS_CLASS[status] || "bg-slate-100 text-slate-600"}`} data-testid="assist-status">
    {STATUS_LABELS[status] || status}
  </span>
);

// What the pharmacist can share: their screen and voice, or voice only (e.g. a phone that cannot share its screen).
const ShareKind = ({ screenShare }) => (screenShare ? (
  <span className="inline-flex items-center gap-1 text-xs text-slate-600"><MonitorUp size={14} aria-hidden="true" />Écran + audio</span>
) : (
  <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Mic size={14} aria-hidden="true" />Audio seul</span>
));

// Free text typed by the pharmacist: rendered as text only (React escapes it), clamped, never as HTML.
const Reason = ({ text, lines = "line-clamp-2" }) => (
  <p className={`text-sm text-slate-700 whitespace-pre-line break-words ${lines}`} data-testid="assist-reason">{text || "Aucun motif"}</p>
);

function useAssistList(path, { poll, pick = (data) => data }) {
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!silent) setPhase("loading");
    try {
      const { data } = await api.get(path);
      if (mine !== seq.current) return;
      setItems(pick(data));
      setPhase("ready");
      setStale(false);
    } catch (e) {
      if (mine !== seq.current) return;
      // A failed refresh keeps the list on screen and says so; only a failed first load replaces it by an error.
      if (silent) {
        setStale(true);
      } else {
        setError(assistError(e).message);
        setPhase("error");
      }
    }
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);
  useVisiblePolling(() => load({ silent: true }), QUEUE_POLL_MS, poll);
  return { items, setItems, phase, error, stale, load };
}

function LoadingRows({ testid }) {
  return (
    <div className="space-y-2" data-testid={testid}><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>
  );
}

function ErrorCard({ message, onRetry, testid }) {
  return (
    <Card className="p-6 text-center space-y-3" data-testid={testid}>
      <AlertTriangle className="mx-auto text-amber-500" size={20} />
      <p className="text-sm text-slate-700">{message}</p>
      <Button variant="outline" onClick={onRetry}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
    </Card>
  );
}

function EmptyCard({ title, text, testid }) {
  return (
    <Card className="p-8 text-center space-y-2" data-testid={testid}>
      <Headset className="mx-auto text-slate-300" size={26} />
      <h3 className="font-medium text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">{text}</p>
    </Card>
  );
}

const StaleNotice = () => (
  <p className="flex items-center gap-1.5 text-xs text-amber-700" role="status" data-testid="assist-stale">
    <AlertTriangle size={14} aria-hidden="true" />Actualisation impossible pour le moment : la liste affichée peut ne plus être à jour.
  </p>
);

// --- File d'attente ---

function QueueTab({ onCount }) {
  const navigate = useNavigate();
  const { items, setItems, phase, error, stale, load } = useAssistList("/assist/requests", { poll: true });
  const mine = useAssistList("/assist/requests/mine", { poll: true });
  const colleagues = useAssistList("/assist/requests/live", { poll: true, pick: (data) => data.items || [] });
  const [accepting, setAccepting] = useState(null);

  useEffect(() => { if (phase === "ready") onCount(items.length); }, [phase, items.length, onCount]);

  const accept = async (item) => {
    if (accepting) return;
    setAccepting(item.id);
    try {
      await api.post(`/assist/requests/${item.id}/accept`);
      toast.success(`Demande de ${item.pharmacy} prise en charge`);
      notifyQueueChanged();
      navigate(assistSessionPath(item.id));
    } catch (e) {
      const { code, message } = assistError(e);
      if (code === "already_accepted" || code === "invalid_transition" || code === "request_not_found") {
        // Someone was quicker or the pharmacist cancelled: not an error on our side, just a list that moved.
        toast(message);
        setItems((prev) => prev.filter((i) => i.id !== item.id));
        load({ silent: true });
        notifyQueueChanged();
      } else {
        toast.error(message);
      }
    } finally {
      setAccepting(null);
    }
  };

  if (phase === "loading" || mine.phase === "loading") return <LoadingRows testid="assist-queue-loading" />;
  if (phase === "error") return <ErrorCard message={error} onRetry={() => load()} testid="assist-queue-error" />;
  const inProgress = (
    <>
      <InProgressSection list={mine} />
      <ColleaguesSection list={colleagues} onJoined={() => { mine.load({ silent: true }); colleagues.load({ silent: true }); }} />
    </>
  );
  const others = mine.items.length > 0 || colleagues.items.length > 0;
  if (items.length === 0) {
    return (
      <div className="space-y-4">
        {inProgress}
        <div className="space-y-2">
          {others && <SectionTitle title="En attente" count={0} />}
          {stale && <StaleNotice />}
          {others ? (
            <p className="text-sm text-slate-500" data-testid="assist-queue-empty">Aucune demande en attente pour le moment.</p>
          ) : (
            <EmptyCard title="Aucune demande en attente" testid="assist-queue-empty"
              text="Les demandes d'assistance des pharmacies apparaissent ici dès qu'elles sont envoyées. La liste se met à jour toute seule." />
          )}
        </div>
      </div>
    );
  }

  const acceptButton = (item, className = "") => (
    <Button size="sm" onClick={() => accept(item)} disabled={!!accepting} className={className} data-testid="assist-accept">
      {accepting === item.id && <Loader2 size={14} className="mr-1.5 animate-spin" />}Prendre en charge
    </Button>
  );

  return (
    <div className="space-y-4">
      {inProgress}
      <div className="space-y-2">
        {others && <SectionTitle title="En attente" count={items.length} />}
        {stale && <StaleNotice />}
        <Card className="hidden md:block overflow-hidden" data-testid="assist-queue-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pharmacie</TableHead><TableHead>Motif</TableHead><TableHead>Attente</TableHead><TableHead>Partage</TableHead>
                <TableHead className="text-right"><span className="sr-only">Action</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid="assist-queue-row" data-request-id={item.id}>
                  <TableCell className="align-top">
                    <div className="font-medium text-slate-900">{item.pharmacy}</div>
                    <div className="text-xs text-slate-500">{item.requester}</div>
                  </TableCell>
                  <TableCell className="align-top max-w-md"><Reason text={item.reason} /></TableCell>
                  <TableCell className="align-top text-sm text-slate-600 whitespace-nowrap">{waitingSince(item.created_at)}</TableCell>
                  <TableCell className="align-top"><ShareKind screenShare={item.screen_share} /></TableCell>
                  <TableCell className="align-top text-right">{acceptButton(item)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <div className="md:hidden space-y-2" data-testid="assist-queue-cards">
          {items.map((item) => (
            <Card key={item.id} className="p-3 space-y-2" data-testid="assist-queue-row" data-request-id={item.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">{item.pharmacy}</div>
                  <div className="text-xs text-slate-500">{item.requester} · {waitingSince(item.created_at)}</div>
                </div>
                <ShareKind screenShare={item.screen_share} />
              </div>
              <Reason text={item.reason} lines="line-clamp-3" />
              {acceptButton(item, "w-full")}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- En cours (en tête de la file d'attente) ---

const SectionTitle = ({ title, count }) => (
  <h2 className="text-sm font-semibold text-slate-900">{title}{count > 0 && <span className="ml-1.5 font-normal text-slate-500">({count})</span>}</h2>
);

// The assistances this technician has taken and not finished, above the waiting requests; nothing at all when there are none.
function InProgressSection({ list }) {
  const { items, phase, error, stale, load } = list;
  if (phase === "error") {
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm text-amber-800" role="alert" data-testid="assist-mine-error">
        <AlertTriangle size={14} aria-hidden="true" />Vos assistances en cours n'ont pas pu être chargées : {error}
        <Button variant="link" size="sm" className="h-auto p-0" onClick={() => load()}>Réessayer</Button>
      </p>
    );
  }
  if (items.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="assist-mine">
      <SectionTitle title="Mes assistances en cours" count={items.length} />
      {stale && <StaleNotice />}
      <Card className="overflow-hidden border-jamora/30" data-testid="assist-mine-list">
        {items.map((item) => (
          <Link key={item.id} to={assistSessionPath(item.id)} data-testid="assist-mine-row"
            className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-sm text-slate-900 truncate">{item.pharmacy}</span>
                <StatusChip status={item.status} />
              </div>
              <div className="text-xs text-slate-500 truncate">
                {isInvited(item.status)
                  ? `proposée à ${item.account || item.requester} · envoyée ${waitingSince(item.created_at)}`
                  : <>{item.my_role === "co" ? `avec ${item.technician?.name} · ` : `${item.requester} · `}prise en charge {waitingSince(item.accepted_at)}</>}
              </div>
            </div>
            <span className="hidden sm:inline text-sm font-medium text-jamora">{isInvited(item.status) ? "Voir" : "Reprendre"}</span>
            <ChevronRight size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
          </Link>
        ))}
      </Card>
    </div>
  );
}

// Colleagues' assistances that can be joined: only in multi-technician mode (the server answers an empty list otherwise).
function ColleaguesSection({ list, onJoined }) {
  const navigate = useNavigate();
  const { items, setItems, stale, load } = list;
  const [joining, setJoining] = useState(null);
  if (items.length === 0) return null;

  const join = async (item) => {
    if (joining) return;
    setJoining(item.id);
    try {
      await api.post(`/assist/requests/${item.id}/join`);
      toast.success(`Vous rejoignez l'assistance de ${item.pharmacy}`);
      onJoined();
      navigate(assistSessionPath(item.id));
    } catch (e) {
      const { code, message } = assistError(e);
      if (code === "already_joined") {
        navigate(assistSessionPath(item.id));
      } else if (["session_full", "not_joinable", "multi_disabled", "request_not_found"].includes(code)) {
        toast(message);
        if (code !== "session_full") setItems((prev) => prev.filter((i) => i.id !== item.id));
        load({ silent: true });
      } else {
        toast.error(message);
      }
    } finally {
      setJoining(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="assist-colleagues">
      <SectionTitle title="En cours chez vos collègues" count={items.length} />
      {stale && <StaleNotice />}
      <Card className="overflow-hidden" data-testid="assist-colleagues-list">
        {items.map((item) => {
          const technicians = presentTechnicians(item).map((t) => t.name).join(", ");
          const full = item.places_left <= 0;
          return (
            <div key={item.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0"
              data-testid="assist-colleague-row" data-request-id={item.id}>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-sm text-slate-900 truncate">{item.pharmacy}</span>
                  <StatusChip status={item.status} />
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {technicians} · prise en charge {waitingSince(item.accepted_at)}
                </div>
              </div>
              {full ? (
                <span className="text-xs font-medium text-slate-500" data-testid="assist-colleague-full">Complet</span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => join(item)} disabled={!!joining} className="w-full sm:w-auto"
                  data-testid="assist-join">
                  {joining === item.id ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UserPlus size={14} className="mr-1.5" />}
                  Rejoindre
                </Button>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// --- Historique ---

function HistoryTab({ seesAll, selfId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get("/assist/history");
      setItems(data.items);
      setCursor(data.next_cursor || null);
      setPhase("ready");
    } catch (e) {
      setError(assistError(e).message);
      setPhase("error");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/assist/history", { params: { cursor } });
      setItems((prev) => [...prev, ...data.items.filter((i) => !prev.some((p) => p.id === i.id))]);
      setCursor(data.next_cursor || null);
    } catch (e) {
      toast.error(assistError(e).message);
    } finally {
      setLoadingMore(false);
    }
  };

  if (phase === "loading") return <LoadingRows testid="assist-history-loading" />;
  if (phase === "error") return <ErrorCard message={error} onRetry={load} testid="assist-history-error" />;
  if (items.length === 0) {
    return <EmptyCard title="Aucune assistance terminée" testid="assist-history-empty"
      text={seesAll ? "Les assistances terminées de toute l'équipe apparaîtront ici." : "Vos assistances terminées apparaîtront ici."} />;
  }
  return (
    <div className="space-y-2">
      <Card className="hidden md:block overflow-hidden" data-testid="assist-history-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pharmacie</TableHead>{seesAll && <TableHead>Technicien</TableHead>}<TableHead>Issue</TableHead>
              <TableHead>Demandée le</TableHead><TableHead>Durée</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              // The whole line opens the detail; the pharmacy name is the real link (keyboard, middle click).
              <TableRow key={item.id} data-testid="assist-history-row" className="cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(assistSessionPath(item.id))}>
                <TableCell>
                  <Link to={assistSessionPath(item.id)} onClick={(e) => e.stopPropagation()}
                    className="font-medium text-slate-900 hover:underline" data-testid="assist-history-open">{item.pharmacy}</Link>
                  <div className="text-xs text-slate-500">{item.requester}</div>
                </TableCell>
                {seesAll && (
                  <TableCell className="text-sm text-slate-700" data-testid="assist-history-technicians">
                    {historyTechnicians(item).length
                      ? historyTechnicians(item).map((name) => <div key={name}>{name}</div>)
                      : "Non attribué"}
                  </TableCell>
                )}
                <TableCell className="text-sm text-slate-700 max-w-xs">
                  {endedSummary(item)}
                  {/* Without the « Technicien » column (one's own history), the colleagues are named here. */}
                  {!seesAll && coTechniciansText(item, selfId) && <div className="text-xs text-slate-500">{coTechniciansText(item, selfId)}</div>}
                </TableCell>
                <TableCell className="text-sm text-slate-600 whitespace-nowrap">{formatDateTime(item.created_at)}</TableCell>
                <TableCell className="text-sm text-slate-600 whitespace-nowrap">{sessionDuration(item)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Card className="md:hidden overflow-hidden" data-testid="assist-history-cards">
        {items.map((item) => (
          <Link key={item.id} to={assistSessionPath(item.id)}
            className="block px-3 py-2.5 border-b border-slate-100 last:border-b-0 space-y-1 hover:bg-slate-50" data-testid="assist-history-row">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm text-slate-900 truncate">{item.pharmacy}</span>
              <span className="text-xs text-slate-500 shrink-0">{sessionDuration(item)}</span>
            </div>
            <div className="text-xs text-slate-600">{endedSummary(item)}{!seesAll && coTechniciansText(item, selfId) && ` · ${coTechniciansText(item, selfId)}`}</div>
            <div className="text-xs text-slate-500">{formatDateTime(item.created_at)}{seesAll && historyTechnicians(item).length ? ` · ${historyTechnicians(item).join(", ")}` : ""}</div>
          </Link>
        ))}
      </Card>
      {cursor && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="assist-history-more">
            {loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus
          </Button>
        </div>
      )}
    </div>
  );
}

// Browser notifications for new requests: asked only on this explicit click, never when the CRM opens.
function DesktopAlerts() {
  const [permission, setPermission] = useState(notificationPermission);
  if (permission === "unsupported") return null;
  if (permission === "granted") {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="assist-alerts-on">
        <Bell size={14} aria-hidden="true" />Alertes du navigateur activées
      </p>
    );
  }
  if (permission === "denied") {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="assist-alerts-denied">
        <BellOff size={14} aria-hidden="true" />Alertes bloquées par le navigateur : autorisez les notifications pour ce site dans ses réglages.
      </p>
    );
  }
  const enable = async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === "granted") toast.success("Vous serez alerté des nouvelles demandes, même dans un autre onglet.");
  };
  return (
    <Button variant="outline" size="sm" onClick={enable} data-testid="assist-alerts-enable">
      <Bell size={14} className="mr-1.5" aria-hidden="true" />Activer les alertes du navigateur
    </Button>
  );
}

export default function Assistance() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const handles = hasPermission(user, "support:handle");
  const tabs = TABS.filter((t) => handles || !t.handlerOnly);
  const defaultTab = tabs[0].id;
  // A report opened from the global search (router state, consumed once): its tab, with its drawer open — also when the page
  // is already open (hence a fresh object each time, never just the id).
  const [openNote, setOpenNote] = useState(null);
  useEffect(() => {
    const id = location.state?.openNote;
    if (!id) return;
    setOpenNote({ id });
    navigate(`${location.pathname}?tab=notes`, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);
  const requested = location.state?.openNote ? "notes" : searchParams.get("tab");
  const tab = tabs.some((t) => t.id === requested) ? requested : defaultTab;
  const [queueCount, setQueueCount] = useState(null);
  const seesAll = hasPermission(user, "support:view_all");
  const initiates = canInitiateAssist(user);
  const [inviteOpen, setInviteOpen] = useState(false);
  // « Lancer une assistance » from the global search: opens the dialog once, then cleans the address.
  useEffect(() => {
    if (searchParams.get("invite") !== "1") return;
    if (initiates) setInviteOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, initiates]);

  return (
    <div className="space-y-4" data-testid="assist-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Assistance à distance</h1>
          <p className="text-sm text-slate-500">
            {handles ? "Prenez en charge les demandes d'aide des pharmacies et voyez leur écran, sans jamais en prendre le contrôle."
              : "Consultez les assistances à distance de l'équipe et leurs comptes rendus."}
          </p>
        </div>
        {initiates && (
          <Button className="shrink-0" onClick={() => setInviteOpen(true)} data-testid="assist-invite-open">
            <Send size={14} className="mr-1.5" aria-hidden="true" />Lancer une assistance
          </Button>
        )}
      </div>
      {handles && <DesktopAlerts />}
      {initiates && <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />}

      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Vue" data-testid="assist-tabs">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} data-testid={`assist-tab-${t.id}`}
            onClick={() => setSearchParams(t.id === defaultTab ? {} : { tab: t.id }, { replace: true })}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === t.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {t.label}
            {t.id === "queue" && queueCount > 0 && (
              <span className={`rounded-full px-1.5 text-2xs font-semibold ${tab === t.id ? "bg-white/20" : "bg-amber-100 text-amber-800"}`} data-testid="assist-queue-count">{queueCount}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "queue" && <QueueTab onCount={setQueueCount} />}
      {tab === "history" && <HistoryTab seesAll={seesAll} selfId={user?.id} />}
      {tab === "notes" && <AssistNotesList user={user} openRequest={openNote} />}
    </div>
  );
}
